import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Student } from '../types';

const STUDENTS_COLLECTION = 'students';

// Check if Firebase is configured
const isFirebaseConfigured = () => {
  return process.env.REACT_APP_FIREBASE_API_KEY !== 'your_api_key_here';
};

class StudentService {
  // Get all students for a teacher
  async getStudents(teacherId?: string, className?: string, schoolYear?: string): Promise<Student[]> {
    try {
      if (isFirebaseConfigured()) {
        const studentsRef = collection(db, STUDENTS_COLLECTION);
        let q = query(studentsRef, orderBy('name'));
        
        if (teacherId) {
          q = query(studentsRef, where('teacherId', '==', teacherId), orderBy('name'));
        }
        
        const querySnapshot = await getDocs(q);
        let students = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date()
        })) as Student[];
        
        // Apply additional filters
        if (className) {
          students = students.filter(student => student.className === className);
        }
        if (schoolYear) {
          students = students.filter(student => student.schoolYear === schoolYear);
        }
        
        return students;
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem('students');
        const students = stored ? JSON.parse(stored) : [];
        let filteredStudents = students.filter((student: Student) => !teacherId || student.teacherId === teacherId);
        
        // Apply additional filters
        if (className) {
          filteredStudents = filteredStudents.filter((student: Student) => student.className === className);
        }
        if (schoolYear) {
          filteredStudents = filteredStudents.filter((student: Student) => student.schoolYear === schoolYear);
        }
        
        return filteredStudents;
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      return [];
    }
  }

  // Add a new student
  async addStudent(student: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>): Promise<Student> {
    try {
      if (isFirebaseConfigured()) {
        const studentsRef = collection(db, STUDENTS_COLLECTION);
        const newStudent = {
          ...student,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        };
        
        const docRef = await addDoc(studentsRef, newStudent);
        return {
          id: docRef.id,
          ...newStudent,
          createdAt: newStudent.createdAt.toDate(),
          updatedAt: newStudent.updatedAt.toDate()
        };
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem('students');
        const students = stored ? JSON.parse(stored) : [];
        
        const newStudent: Student = {
          id: `student_${Date.now()}`,
          ...student,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        students.push(newStudent);
        localStorage.setItem('students', JSON.stringify(students));
        return newStudent;
      }
    } catch (error) {
      console.error('Error adding student:', error);
      throw error;
    }
  }

  // Update a student
  async updateStudent(id: string, updates: Partial<Student>): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        const studentRef = doc(db, STUDENTS_COLLECTION, id);
        await updateDoc(studentRef, {
          ...updates,
          className: updates.className || null,
          updatedAt: Timestamp.now()
        });
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem('students');
        const students = stored ? JSON.parse(stored) : [];
        
        const index = students.findIndex((s: Student) => s.id === id);
        if (index !== -1) {
          students[index] = {
            ...students[index],
            ...updates,
            updatedAt: new Date()
          };
          localStorage.setItem('students', JSON.stringify(students));
        }
      }
    } catch (error) {
      console.error('Error updating student:', error);
      throw error;
    }
  }

  // Delete a student
  async deleteStudent(id: string): Promise<void> {
    try {
      if (isFirebaseConfigured()) {
        const studentRef = doc(db, STUDENTS_COLLECTION, id);
        await deleteDoc(studentRef);
      } else {
        // Fallback to localStorage
        const stored = localStorage.getItem('students');
        const students = stored ? JSON.parse(stored) : [];
        
        const filteredStudents = students.filter((s: Student) => s.id !== id);
        localStorage.setItem('students', JSON.stringify(filteredStudents));
      }
    } catch (error) {
      console.error('Error deleting student:', error);
      throw error;
    }
  }

  // Get a single student by ID
  async getStudent(id: string): Promise<Student | null> {
    try {
      const students = await this.getStudents();
      return students.find(student => student.id === id) || null;
    } catch (error) {
      console.error('Error fetching student:', error);
      return null;
    }
  }

  // Assign student to a class
  async assignStudentToClass(studentId: string, classId: string): Promise<void> {
    try {
      await this.updateStudent(studentId, { className: classId });
    } catch (error) {
      console.error('Error assigning student to class:', error);
      throw error;
    }
  }

  // Remove student from class
  async removeStudentFromClass(studentId: string): Promise<void> {
    try {
      await this.updateStudent(studentId, { className: undefined });
    } catch (error) {
      console.error('Error removing student from class:', error);
      throw error;
    }
  }

  // Set student school year
  async setStudentSchoolYear(studentId: string, schoolYear: string): Promise<void> {
    try {
      await this.updateStudent(studentId, { schoolYear });
    } catch (error) {
      console.error('Error setting student school year:', error);
      throw error;
    }
  }

  // Get students by class
  async getStudentsByClass(classId: string): Promise<Student[]> {
    try {
      return await this.getStudents(undefined, classId);
    } catch (error) {
      console.error('Error fetching students by class:', error);
      return [];
    }
  }

  // Get students by school year
  async getStudentsBySchoolYear(schoolYear: string): Promise<Student[]> {
    try {
      return await this.getStudents(undefined, undefined, schoolYear);
    } catch (error) {
      console.error('Error fetching students by school year:', error);
      return [];
    }
  }

  // Get students by teacher and class
  async getStudentsByTeacherAndClass(teacherId: string, classId: string): Promise<Student[]> {
    try {
      return await this.getStudents(teacherId, classId);
    } catch (error) {
      console.error('Error fetching students by teacher and class:', error);
      return [];
    }
  }

  // Get students by teacher and school year
  async getStudentsByTeacherAndSchoolYear(teacherId: string, schoolYear: string): Promise<Student[]> {
    try {
      return await this.getStudents(teacherId, undefined, schoolYear);
    } catch (error) {
      console.error('Error fetching students by teacher and school year:', error);
      return [];
    }
  }
}

export const studentService = new StudentService();
